import "./DashboardActionBarFilters.scss"
import { Button, DateRangeBox, DropDownButton, Popup } from "devextreme-react"
import { useEffect, useRef, useState } from "react"
import { t } from "i18next"
import { useDashboardDateRange } from "../../context/DashboardDateRangeContext"
import { useScreenSize } from "../../../../core/hook/useScreenSize"
import { DateHelper } from "../../../../core/helpers/dateHelper"

export interface ButtonsCollectionAction {
    id: number
    text?: string
    onClick?: () => void
}

interface ButtonsCollectionProps {
    isLoading?: boolean
    dropdownThreshold?: number
}

enum PredefinedDateRange {
    Week = 1,
    Month,
    Year,
    All
}

export const DashboardActionBarFilters: React.FC<ButtonsCollectionProps> = ({dropdownThreshold}) => {
    const dateRangeContext = useDashboardDateRange()
    const containerRef = useRef<HTMLDivElement>(null)
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false)
    const { isXSmall } = useScreenSize()

    const actions: ButtonsCollectionAction[] = [
        { id: PredefinedDateRange.Week, text: t("Week"), onClick: () => dateRangeContext.setDateRange(DateHelper.getCurrentWeekDateRange()) },
        { id: PredefinedDateRange.Month, text: t("Month"), onClick: () => dateRangeContext.setDateRange(DateHelper.getCurrentMonthDateRange()) },
        { id: PredefinedDateRange.Year, text: t("Year"), onClick: () => dateRangeContext.setDateRange(DateHelper.getCurrentYearDateRange()) },
        { id: PredefinedDateRange.All, text: t("All"), onClick: () => dateRangeContext.setDateRange(DateHelper.getAllTimeDateRange()) }
    ]

    const checkOverflow = () => {
        const container = containerRef.current?.parentElement?.parentElement

        if (!container || !dropdownThreshold)
            return

        setShowDropdown(container.offsetWidth < dropdownThreshold)

        setIsPopupVisible(false)
    }

    useEffect(() => {
        const container = containerRef.current
        if (!container)
            return

        checkOverflow()

        const observer = new ResizeObserver(() => {
            requestAnimationFrame(checkOverflow)
        })
        observer.observe(container)

        return () => observer.disconnect()
    }, [actions])

    const predefinedDateRangeButtons = actions.map(action => (
        <Button
            key={action.id}
            className="buttons-collection-button"
            text={action.text}
            stylingMode="text"
            onClick={() => action.onClick?.()}
        />
    ))

    const dateRangeDropdown = (
        <DropDownButton
            dropDownOptions={{ width: 150 }}
            icon="menu"
            activeStateEnabled
            stylingMode="text"
            items={actions}
            onItemClick={(e) => actions.find(action => action.id === e.itemData.id)?.onClick?.()}
            displayExpr="text"
            keyExpr="id" />
    )

    return (
        <>
            {isXSmall
                ? <>
                    <Button icon="filter" onClick={() => setIsPopupVisible(prev => !prev)} />
                    <Popup
                        visible={isPopupVisible}
                        title={t("Filters")}
                        onHiding={() => setIsPopupVisible(false)}
                        hideOnOutsideClick
                        showCloseButton
                        resizeEnabled={false}
                        dragEnabled={false}>
                        <div className="dashboard-filters-popup">
                            <DateRangeBox
                                startDateText={t("Start Date")}
                                stylingMode="outlined"
                                showClearButton
                                calendarOptions={{ firstDayOfWeek: 1 }}
                                onValueChanged={(e) => dateRangeContext.setDateRange(e.value)}
                                value={dateRangeContext.dateRange} />
                            <div className="dashboard-filters-predefined-date-ranges mt-1 mobile">
                                {predefinedDateRangeButtons}
                            </div>
                            <div className="dashboard-filters-footer">
                                <Button text={t("Close")} onClick={() => setIsPopupVisible(false)} />
                                <Button type="default" text={t("Ok")} onClick={() => setIsPopupVisible(false)} />
                            </div>
                        </div>

                    </Popup>
                </>
                : <>
                    <DateRangeBox
                        stylingMode="outlined"
                        startDateLabel={t("Start Date")}
                        endDateLabel={t("End Date")}
                        showClearButton
                        calendarOptions={{ firstDayOfWeek: 1 }}
                        onValueChanged={(e) => dateRangeContext.setDateRange(e.value)}
                        value={dateRangeContext.dateRange} />
                    <div className="buttons-collection-container" ref={containerRef}>
                        {showDropdown
                            ? dateRangeDropdown
                            : predefinedDateRangeButtons}
                    </div>
                </>}
        </>
    )
}
